<template>
  <BaseCard title="An- und Ummeldung einer Wohnung/eines Hauses">
    <template #content>
      <loading
        :active.sync="isBusy"
        loader="dots"
        blur="20px"
        :is-full-page="false"
      />
      <div class="grid">
        <BaseSelect
          label="Energieart"
          :items="branches"
          :value="selectedBranch"
          :error="errors.get('branch')"
          outlined
          rounded
          searchable
          class="col-span-12 lg:col-span-6"
          @update="updateItem(['selectedBranch', $event])"
        />
        <BaseInput
          label="Ihre Postleitzahl"
          :value="zip"
          :error="errors.get('zip')"
          outlined
          rounded
          icon-left="fa-regular fa-location-dot"
          class="col-span-12 lg:col-span-6"
          @update="updateItem(['zip', $event])"
        />
        <template v-if="showAll">
          <BaseSelect
            :key="`io-shop-calculator-${formUpdateKey}--city`"
            label="Stadt*"
            :items="cities"
            :value="city"
            :error="errors.get('city')"
            outlined
            rounded
            searchable
            class="col-span-12 lg:col-span-5"
            @update="updateItem(['city', $event])"
          />
          <BaseSelect
            label="Straße*"
            :items="streets"
            :value="street"
            :error="errors.get('street')"
            outlined
            rounded
            searchable
            class="col-span-12 lg:col-span-5"
            @update="updateItem(['street', $event])"
          />
          <BaseInput
            label="Hausnummer*"
            type="number"
            :value="streetNumber"
            :error="errors.get('streetNumber')"
            outlined
            rounded
            class="col-span-12 lg:col-span-2"
            @update="updateItem(['streetNumber', ($event || '').toString()])"
          />
          <template v-if="selectedBranch === 'heat'">
            <BaseInput
              label="Verbrauch HT*"
              type="number"
              :value="usageHt"
              :error="errors.get('usageHt')"
              outlined
              rounded
              class="col-span-12 lg:col-span-6"
              @update="updateItem(['usageHt', ($event || '').toString()])"
            />
            <BaseInput
              label="Verbrauch NT*"
              type="number"
              :value="usageNt"
              :error="errors.get('usageNt')"
              outlined
              rounded
              class="col-span-12 lg:col-span-6"
              @update="updateItem(['usageNt', ($event || '').toString()])"
            />
          </template>
          <BaseSelect
            v-else
            label="Verbrauch*"
            :items="personsUsage"
            :value="usage"
            :error="errors.get('usage')"
            outlined
            rounded
            class="col-span-12"
            @update="updateItem(['usage', $event])"
          />
        </template>
      </div>
      <BaseButtonV2
        type="filled"
        color="primary"
        class="button"
        :disabled="!sendable"
        @click="submitCalculator"
      >
        Preis berechnen
      </BaseButtonV2>
    </template>
  </BaseCard>
</template>

<script>
import Loading from 'vue-loading-overlay';
import axios from 'axios';
import FormErrors from '../utils/FormErrors';

export default {
  name: 'IoShopCalculator',

  components: {
    Loading,
  },

  props: {
    customerType: {
      required: false,
      type: String,
      validator: (val) => ['private', 'business', 'gas'].indexOf(val) !== -1,
      default: () => 'private',
    },
    branch: {
      required: false,
      type: String,
      validator: (val) => ['power', 'heat', 'gas'].indexOf(val) !== -1,
      default: () => 'power',
    },
    enetAction: {
      required: true,
      type: String,
    },
    calculatorAction: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      formUpdateKey: 1,
      sendable: false,
      selectedBranch: this.branch,
      zip: '',
      currentZip: null,
      city: null,
      street: null,
      streetNumber: '',
      usage: null,
      usageHt: null,
      usageNt: null,
      cities: [],
      streets: [],
      isBusy: false,
      errors: new FormErrors(),
      branches: [
        {
          value: 'power',
          label: 'Strom für Zuhause',
          icon: 'fa-light fa-bolt-lightning',
        },
        {
          value: 'heat',
          label: 'Strom zum Heizen',
          icon: 'fa-solid fa-heat',
        },
      ],
      personsUsage: [
        {
          label: '1 Personen (1500)',
          value: 1500,
        },
        {
          label: '2 Personen (2500)',
          value: 2500,
        },
        {
          label: '3 Personen (3500)',
          value: 3500,
        },
        {
          label: '4 Personen (4500)',
          value: 4500,
        },
      ],
    };
  },

  computed: {
    showAll() {
      return this.cities?.length;
    },
  },
  watch: {
    zip(newZip) {
      if (newZip.length === 5) {
        this.getCitiesAndStreets();
      }
      this.checkIfValid();
    },
    city() {
      this.checkIfValid();
    },
    street() {
      this.checkIfValid();
    },
    streetNumber() {
      this.checkIfValid();
    },
    usage() {
      this.checkIfValid();
    },
    usageHt() {
      this.checkIfValid();
    },
    usageNt() {
      this.checkIfValid();
    },
    selectedBranch() {
      this.checkIfValid();
    },
  },

  methods: {
    checkIfValid() {
      let isValid = this.zip.length === 5;
      isValid = isValid && [null, ''].indexOf(this.city) === -1;
      isValid = isValid && [null, ''].indexOf(this.street) === -1;
      isValid = isValid && this.streetNumber.trim() !== '';

      if (this.selectedBranch === 'heat') {
        isValid = isValid
          && parseInt(this.usageHt, 10) >= 1
          && parseInt(this.usageHt, 10) < 100000;
        isValid = isValid
          && parseInt(this.usageNt, 10) >= 1
          && parseInt(this.usageNt, 10) < 100000;
      } else {
        isValid = isValid && parseInt(this.usage, 10) >= 1 && parseInt(this.usage, 10) < 100000;
      }

      this.sendable = isValid;
    },
    async getCitiesAndStreets() {
      if (this.zip === this.currentZip) return;

      this.isBusy = true;
      try {
        const { zip } = this;
        let response = await axios.get(this.enetAction, {
          params: {
            postcode: zip,
          },
        });
        response = response.data;
        this.cities = this.mapItems(response.cities);
        this.streets = this.mapItems(response.streets);
        const citiesFlat = this.cities.map((city) => city.value);
        const streetsFlat = this.streets.map((street) => street.value);

        if (!citiesFlat.includes(this.city) && this.cities.length) {
          this.city = this.cities[0].value;
        }
        if (!streetsFlat.includes(this.street) && this.streets.length) {
          this.street = this.streets[0].value;
        }
        this.currentZip = zip;
      } catch (err) {
        this.cities = [];
        this.streets = [];
        this.errors.set('zip', 'Ungültige Postleitzahl');
        this.currentZip = null;
      } finally {
        this.isBusy = false;
      }
      this.formUpdateKey += 1;
    },
    mapItems(items) {
      return (items || []).map((item) => ({
        value: item.id,
        label: item.name,
      }));
    },
    translatedBranch() {
      switch (this.selectedBranch) {
        case 'gas':
          return 'gas';
        case 'heat':
          return 'waerme';
        default:
          return 'strom';
      }
    },
    translatedCustomerType() {
      if (this.customerType === 'business') {
        return 'gewerbe';
      }

      return 'privat';
    },
    urlUsage() {
      if (this.selectedBranch === 'heat') {
        return `${this.usageHt}/${this.usageNt}`;
      }

      return this.usage;
    },
    submitCalculator() {
      let action = this.calculatorAction;
      action = action.replace('[USAGE]', this.urlUsage());
      action = action.replace('[ZIP]', this.zip);
      action = action.replace('[CUSTOMER_TYPE]', this.translatedCustomerType);
      action = action.replace('[BRANCH]', this.translatedBranch());
      action = action.replace('[CITY]', this.city);
      action = action.replace('[STREET]', this.street);
      action = action.replace('[STREETNUMBER]', this.streetNumber);

      window.location.href = action;
    },
    updateItem([key, value]) {
      this.errors.clear(key);
      this[key] = value;
    },
  },
};
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.button {
  width: fit-content !important;
  margin-left: auto !important;
}
</style>
